import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { relayStylePagination } from "@apollo/client/utilities";
import {
  SHOPIFY_GRAPHQL_API_VERSION_HEADER,
  ShopifyGraphqlApiVersion,
} from "@intelligems/ig-types/functions/shopify/shopify-graphql";

import { getAuthToken } from "../utils";

const httpLink = createHttpLink({
  uri: `${
    import.meta.env.VITE_GRAPHQL_URL || import.meta.env.VITE_BASE_URL
  }/v2/shopify/graphql`,
});

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions,@typescript-eslint/no-base-to-string
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  }
  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const authLink = setContext((_, { headers, shopifyApiVersion }) => {
  return {
    headers: {
      ...headers,
      Authorization: getAuthToken(),
      ...(shopifyApiVersion
        ? {
            [SHOPIFY_GRAPHQL_API_VERSION_HEADER]:
              shopifyApiVersion as ShopifyGraphqlApiVersion,
          }
        : {}),
    },
  };
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, operation) =>
      !!error && !operation.getContext().disableRetry,
  },
});

/**
 * Cache policies for Shopify Relay-style pagination.
 * Full explanation: https://www.notion.so/intelligems/Apollo-Cache-Type-Policies-for-Shopify-GraphQL-3252b37220928066af48f975395a8c26
 *
 * Rules:
 * 1. Always use shopifyPagination() — never relayStylePagination() directly.
 *    relayStylePagination's read function returns all accumulated edges ignoring
 *    args, causing stale data when re-querying with different filters.
 * 2. Pass ["query", "sortKey"] (or whichever filter args apply) for fields that
 *    accept them. Omit keyArgs only for fields that always return the same list
 *    (e.g. productTypes).
 * 3. Use fetchPolicy: "network-only" for queries driven by frequently-changing
 *    user input (search, filters) to avoid cache bloat.
 */
const shopifyPagination = (
  keyArgs?: Parameters<typeof relayStylePagination>[0]
) => {
  const pagination = relayStylePagination(keyArgs);
  delete pagination.read;
  return pagination;
};

const shopifyTypePolicies: InMemoryCacheConfig["typePolicies"] = {
  Query: {
    fields: {
      products: shopifyPagination(["query", "sortKey"]),
      productTypes: shopifyPagination(),
      productTags: shopifyPagination(),
      productVendors: shopifyPagination(),
      deliveryProfiles: relayStylePagination(),
      collections: shopifyPagination(["query", "sortKey"]),
      pages: shopifyPagination(["query", "sortKey"]),
      articles: shopifyPagination(["query", "sortKey"]),
      files: shopifyPagination(["query"]),
    },
  },
  Product: {
    fields: {
      variants: relayStylePagination(),
    },
  },
};

export const createShopifyCache = () =>
  new InMemoryCache({ typePolicies: shopifyTypePolicies });

export const shopifyGqlClient = new ApolloClient({
  devtools: {
    enabled: true,
  },
  link: authLink.concat(retryLink).concat(errorLink).concat(httpLink),
  cache: createShopifyCache(),
});
